import React, { useEffect, useState, useRef } from 'react';
import Chart from 'chart.js/auto';
import API_BASE_URL from '../api';
import { authFetch } from '../authFetch';
import { useAuth } from '../authContext';
import useSelectedRadio from '../hooks/useSelectedRadio';
import { useTranslation } from 'react-i18next';
import { useLocale, fmtNumber } from '../i18n/format';
import ExportButton from '../components/ExportButton';
import { buildNewListenersDoc } from "../pdf/builders/new_listeners/buildDoc";
import { buildNewListenersWorkbook } from "../excel/builders/new_listeners/buildWorkbook";

export default function AdrServicePage() {
  const [period, setPeriod] = useState('week');   // тиждень / місяць / квартал
  const [date1, setDate1] = useState('');
  const [data, setData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [total, setTotal] = useState(0);          // средний ADR за период
  const [info, setInfo] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [geo, setGeo] = useState([]);             // оставляем только для экспорта, на экране не показываем

  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const exportBlockRef = useRef(null);
  const { isAuth } = useAuth();
  const { radioId, shortcode, name: stationName } = useSelectedRadio();
  const { t } = useTranslation('common');
  const locale = useLocale();

  // по умолчанию — сегодня
  useEffect(() => {
    if (!date1) {
      const today = new Date();
      setDate1(today.toISOString().slice(0, 10));
    }
  }, [date1]);

  const calcRange = () => {
    if (!date1) return null;
    const endDate = new Date(date1);
    if (Number.isNaN(endDate.getTime())) return null;

    const from = new Date(endDate);
    if (period === 'week') {
      from.setDate(endDate.getDate() - 6);       // 7 дней
    } else if (period === 'month') {
      from.setMonth(endDate.getMonth() - 1);     // ~1 месяц
    } else if (period === 'quarter') {
      from.setMonth(endDate.getMonth() - 3);     // ~3 месяца
    }

    const dateFromStr = from.toISOString().slice(0, 10);
    const dateToStr = endDate.toISOString().slice(0, 10);
    return { dateFromStr, dateToStr };
  };

  const fetchData = async () => {
    if (!isAuth || !radioId || !date1) return;

    const range = calcRange();
    if (!range) {
      setInfo(t('common.invalid_date') || 'Invalid date');
      return;
    }

    const { dateFromStr, dateToStr } = range;

    setInfo(t('common.loading'));
    try {
      const API = (API_BASE_URL || '').replace(/\/+$/, '');
      const url = `${API}/radios/${encodeURIComponent(radioId)}/metrics/adr/daily?date_from=${dateFromStr}&date_to=${dateToStr}`;
      const resp = await authFetch(url);
      const json = await resp.json();

      if (json.error || json.detail) {
        setInfo(json.error || json.detail || 'Error');
        setData([]); setLabels([]); setGeo([]); setTotal(0);
        setStart(''); setEnd('');
        return;
      }

      const items = json.items || [];
      const lbls = items.map(r => r.day);
      const vals = items.map(r => r.listeners_unique || 0);
      const daysCount = vals.length;
      const avgAdr = daysCount ? Math.round(vals.reduce((a, b) => a + b, 0) / daysCount) : 0;

      setData(vals);
      setLabels(lbls);
      setTotal(avgAdr);
      setGeo([]); // гео для ADR не считаем
      setStart(json.date_from || (lbls[0] || ''));
      setEnd(json.date_to || (lbls[lbls.length - 1] || ''));
      setInfo('');

      setTimeout(() => drawChart({ labels: lbls, data: vals }), 50);
    } catch {
      setInfo(t('common.network_error'));
      setData([]); setLabels([]); setGeo([]); setTotal(0);
      setStart(''); setEnd('');
    }
  };

  function drawChart(resp) {
    if (!Chart || !chartRef.current || !resp.data) return;
    const ctx = chartRef.current.getContext('2d');
    if (chartInstance.current) chartInstance.current.destroy();

    const drawLabels = resp.labels || [];
    const drawData = resp.data || [];

    chartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: drawLabels,
        datasets: [{
          label: t('adr.chart_label') || 'ADR',
          data: drawData,
          backgroundColor: '#36a2eb'
        }]
      },
      options: {
        responsive: true,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true } }
      }
    });
  }

  const clean = (value) => String(value ?? '').replace(/[^0-9A-Za-z_-]/g, '-');
  const filenameBase = `adr_${clean(shortcode || 'channel')}_${clean(period || 'period')}_${clean(date1 || '')}`;
  const exportDisabled = !data?.length || !!info;
  const exportButtonStyle = calcExportButtonStyle(exportDisabled);

  if (!radioId) {
    return (
      <div style={widgetStyle}>
        <h2 style={{ marginBottom: 8 }}>{t('adr.title') || 'ADR (Average Daily Reach)'}</h2>
        <div style={{ color: '#e54848', fontWeight: 600, fontSize: 18 }}>
          {t('common.no_channel')}
        </div>
      </div>
    );
  }

  return (
    <div style={widgetStyle}>
      {/* Описание сервиса */}
      <div
        style={{
          width: "100%",
          padding: "12px 14px",
          borderRadius: 8,
          background: "#f8f8f8",
          color: "#1a1a1a",
          fontSize: 16.5,
          margin: "8px 0 20px 0",
          boxSizing: "border-box",
          whiteSpace: "pre-line",
          lineHeight: 1.65
        }}
      >
        {t('adr.desc') || 'ADR показує середню добову аудиторію радіостанції за обраний період.'}
      </div>

      <h2 style={{ marginBottom: 8 }}>{t('adr.title') || 'ADR (Average Daily Reach)'}</h2>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: PERIOD_BUTTON_GAP
        }}
      >
        <div style={{ display: 'inline-flex', gap: PERIOD_BUTTON_GAP }}>
          <button onClick={() => setPeriod('week')} style={periodBtn(period === 'week')}>
            {t('period.week')}
          </button>
          <button onClick={() => setPeriod('month')} style={periodBtn(period === 'month')}>
            {t('period.month')}
          </button>
          <button onClick={() => setPeriod('quarter')} style={periodBtn(period === 'quarter')}>
            {t('period.quarter') || 'Квартал'}
          </button>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: DATE_FIELD_GAP,
            marginLeft: BUTTONS_DATE_SPACING,
            flexShrink: 0,
            minWidth: DATE_BLOCK_MIN_WIDTH
          }}
        >
          <span style={{ minWidth: DATE_LABEL_MIN_WIDTH, fontWeight: 500, textAlign: 'right' }}>
            📅 {t('adr.end_date') || t('new_listeners.end_date')}
          </span>
          <input type="date" value={date1} onChange={e => setDate1(e.target.value)} style={inputStyle}/>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: ACTION_BUTTON_GAP,
          alignItems: 'center',
          marginBottom: 16
        }}
      >
        <button
          type="button"
          onClick={fetchData}
          style={showButtonStyle}
        >
          {t('common.show')}
        </button>
        <ExportButton
          data={data}
          onBeforeBuild={async () => {
            const logoUrl = `${process.env.PUBLIC_URL || ''}/Radiomonitor.NetLogo.png`;
            let logoDataUrl = null;
            try {
              const resp = await fetch(logoUrl, { cache: 'no-store' });
              const blob = await resp.blob();
              const reader = new FileReader();
              logoDataUrl = await new Promise((res, rej) => {
                reader.onloadend = () => res(reader.result);
                reader.onerror = rej;
                reader.readAsDataURL(blob);
              });
            } catch {}
            const chartDataUrl =
              (chartRef.current && chartRef.current.toDataURL && chartRef.current.toDataURL("image/png")) || null;
            return { logoDataUrl, chartDataUrl };
          }}
          buildDoc={(payload) =>
            buildNewListenersDoc({
              t, shortcode, stationName,
              serviceName: t('adr.title') || 'ADR (Average Daily Reach)',
              period, date1,
              start, end, total, labels, data, geo,
              fmtNumber, locale,
              logoDataUrl: payload?.logoDataUrl || null,
              chartDataUrl: payload?.chartDataUrl || null
            })
          }
          buildWorkbook={(XLSX) =>
            buildNewListenersWorkbook(XLSX, {
              t, shortcode, stationName,
              serviceName: t('adr.title') || 'ADR (Average Daily Reach)',
              period, date1,
              start, end, total, labels, data,
              fmtNumber, locale
            })
          }
          filename={`${filenameBase}.pdf`}
          xlsxFilename={`${filenameBase}.xlsx`}
          label={t('export_pdf')}
          labelXlsx={t('export_excel')}
          style={exportButtonStyle}
          disabled={exportDisabled}
        />
      </div>
      <div ref={exportBlockRef}>
        {info && <div style={{ fontWeight: 500, margin: "18px 0", color: 'red' }}>{info}</div>}
        {!info && (
          <>
            <div style={{ fontWeight: 'bold', marginBottom: 10 }}>
              {total > 0
                ? <>
                    {t('adr.total') || 'Середній ADR'}: <b>{fmtNumber(total, locale)}</b><br />
                    {t('adr.period') || t('new_listeners.period')}: {start} — {end}
                  </>
                : t('common.no_data')}
            </div>
            <canvas ref={chartRef} width={500} height={220}
              style={{
                width: '100%',
                height: 220,
                display: 'block',
                border: '1px solid #ddd',
                background: '#fafafa',
                margin: '12px 0',
                borderRadius: 10
              }}
            />
            {/* Гео для ADR не показываем */}
          </>
        )}
      </div>
    </div>
  );
}

const widgetStyle = {
  maxWidth: 1020,
  margin: '40px auto',
  background: '#fff',
  borderRadius: 14,
  padding: 24,
  boxShadow: '0 2px 12px 0 #0001'
};

const inputStyle = {
  padding: 7,
  borderRadius: 4,
  border: '1px solid #ccc',
  fontSize: 16,
  minWidth: 130
};

const PERIOD_BUTTON_WIDTH = 110;
const periodBtn = active => ({
  background: active ? '#36a2eb' : '#f4f4fa',
  color: active ? '#fff' : '#222',
  border: 'none',
  padding: '7px 18px',
  borderRadius: 5,
  fontWeight: 600,
  cursor: 'pointer',
  width: PERIOD_BUTTON_WIDTH
});

const ACTION_BUTTON_WIDTH = 215;
const ACTION_BUTTON_GAP = 10;
const PERIOD_BUTTON_GAP = 8;
const DATE_FIELD_GAP = 8;
const BUTTONS_DATE_SPACING = 16;
const DATE_BLOCK_MIN_WIDTH = 260;
const DATE_LABEL_MIN_WIDTH = 130;

const baseActionButtonStyle = {
  ...periodBtn(false),
  width: ACTION_BUTTON_WIDTH
};

const showButtonStyle = {
  ...baseActionButtonStyle,
  background: '#3867fa',
  color: '#fff'
};

const calcExportButtonStyle = (disabled) => ({
  ...baseActionButtonStyle,
  background: disabled ? '#9bbdfd' : '#3867fa',
  color: '#fff',
  cursor: disabled ? 'not-allowed' : baseActionButtonStyle.cursor
});
